/**
 * Generic auto-reminder data hooks - resource-agnostic, so the same status
 * button + details modal work for transfers, claims and inspections. Parameterised
 * by `resourceType` + `resourceUuid`; backed by the generic
 * `/api/reminders/<type>/<uuid>/` surface. Gated by the `auto_reminders` flag.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/admin/lib/api'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'

export interface AutoReminderStatus {
  resource_type: string
  resource_uuid: string
  label: string
  enabled: boolean
  interval_days: number | null
  max_reminders: number | null
  sent_count: number
  last_sent_at: string | null
  next_send_at: string | null
  is_capped: boolean
  is_due: boolean
  template_name: string | null
  template_slug: string | null
  supports_cadence_override: boolean
  supports_auto_cancel: boolean
  auto_cancel_enabled: boolean
  configured: boolean
  recipient_email?: string
}

export interface SetReminderPayload {
  enabled?: boolean
  interval_days?: number | null
  max_reminders?: number | null
  auto_cancel?: boolean
}

const statusKey = (resourceType: string, uuid: string | null) => [
  'auto-reminder',
  resourceType,
  uuid,
]

/**
 * Reminder status for one record. Fetches only when the `auto_reminders` flag is
 * on and a uuid is present. The parent detail query is the caller's concern; this
 * key is independent so the two never collide.
 */
export function useReminderStatus(
  resourceType: string,
  uuid: string | null,
  options?: { enabled?: boolean },
) {
  const flagOn = useFeatureFlag(FLAGS.AUTO_REMINDERS)
  return useQuery<AutoReminderStatus>({
    queryKey: statusKey(resourceType, uuid),
    queryFn: () =>
      api.get(`/reminders/${resourceType}/${uuid}/`).then((r) => r.data),
    enabled: flagOn && !!uuid && (options?.enabled ?? true),
  })
}

/**
 * Enable / disable / reconfigure a record's reminder. Invalidates the reminder
 * status AND the parent detail query (`[resourceType, uuid]`, the house
 * detail-key convention - e.g. `['transfer', uuid]`) so a record-level
 * "auto reminder on" indicator refreshes too.
 */
export function useSetReminder(resourceType: string) {
  const qc = useQueryClient()
  return useMutation<
    AutoReminderStatus,
    Error,
    { uuid: string } & SetReminderPayload
  >({
    mutationFn: ({ uuid, ...body }) =>
      api.post(`/reminders/${resourceType}/${uuid}/`, body).then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      qc.invalidateQueries({ queryKey: statusKey(resourceType, uuid) })
      qc.invalidateQueries({ queryKey: [resourceType, uuid] })
    },
  })
}

/** Cancel (disable) a record's reminder - the explicit, audited endpoint. */
export function useCancelReminder(resourceType: string) {
  const qc = useQueryClient()
  return useMutation<AutoReminderStatus, Error, { uuid: string }>({
    mutationFn: ({ uuid }) =>
      api
        .post(`/reminders/${resourceType}/${uuid}/cancel/`)
        .then((r) => r.data),
    onSuccess: (_data, { uuid }) => {
      qc.invalidateQueries({ queryKey: statusKey(resourceType, uuid) })
      qc.invalidateQueries({ queryKey: [resourceType, uuid] })
    },
  })
}
